// Per-account override helpers for the post composer.
//
// Model: there is a "base" content (postText, postTitle, selectedFiles, tags,
// platform-specific settings) stored on the page state, plus an
// `accountOverrides` map keyed by account id. Each override is a partial
// object — only fields the user actually changed for that account. The
// "effective" content for an account is `{ ...base, ...override }`.
//
// On submit we group accounts whose effective content is identical and send
// one newPost request per group. This lets a single composer fan out into N
// backend posts without any backend changes.

import { SOCIAL_TYPES } from "./constants";
import { textWithTags } from "utils/threadsText";

// Fields that can be overridden per account.
// Schedule/repeat are intentionally excluded — they're shared across groups.
export const OVERRIDABLE_FIELDS = [
  "postText",
  "postTitle",
  "selectedFiles",
  "tags",
  "youtubePrivacy",
  "youtubeCategoryId",
  "instagramPostType",
  "telegramPinMessage",
  "telegramPinSilent",
  "telegramInlineKeyboard",
  "tiktokPrivacyLevel",
  "tiktokPublishMode",
  "tiktokDisableComment",
  "tiktokDisableDuet",
  "tiktokDisableStitch",
  "tiktokVideoCoverTimestampMs",
  "tiktokPhotoCoverIndex",
  "tiktokAutoAddMusic",
  "tiktokBrandContentToggle",
  "tiktokBrandOrganicToggle",
  // A Threads account's tab can hold its own chain (OP#3469).
  "threadsReplies",
];

// Threads chain parts as they are sent: `key` is a client-only React id and must neither reach the server nor
// split two accounts with the same chain into two requests.
export const toThreadsReplies = (parts) =>
  (Array.isArray(parts) ? parts : [])
    .filter((p) => p && typeof p === "object")
    .map((p) => ({
      text: typeof p.text === "string" ? p.text : "",
      files: Array.isArray(p.files) ? [...p.files] : [],
    }));

// Returns the override map for an account, or an empty object.
export const getOverride = (accountOverrides, accountId) => {
  if (!accountId || !accountOverrides) return {};
  return accountOverrides[accountId] || {};
};

// True if the account has any overridden field.
export const hasOverride = (accountOverrides, accountId) => {
  const ov = getOverride(accountOverrides, accountId);
  return Object.keys(ov).length > 0;
};

// Returns the effective value of a single field for an account.
// If accountId is null, returns the base value.
export const getEffectiveField = (state, field, accountId) => {
  if (!accountId) return state[field];
  const ov = getOverride(state.accountOverrides, accountId);
  return Object.prototype.hasOwnProperty.call(ov, field) ? ov[field] : state[field];
};

// Returns the full effective object for an account (or base if accountId is null).
// Only includes overridable fields.
export const getEffectiveData = (state, accountId) => {
  const out = {};
  for (const f of OVERRIDABLE_FIELDS) {
    out[f] = getEffectiveField(state, f, accountId);
  }
  return out;
};

// Stable JSON stringify (sorted keys) for fingerprinting effective content.
const stableStringify = (value) => {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }
  const keys = Object.keys(value).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(value[k])).join(",") + "}";
};

// Builds the fingerprint that decides which group an account belongs to.
// Two accounts with the same fingerprint will be sent in one request.
//
// Note: every overridable field is part of the fingerprint, even platform-
// specific ones. Two YouTube accounts with the same privacy will group; a
// YouTube and an Instagram account with otherwise-identical text will also
// group (and the resulting request will inject both YouTube and Instagram
// fields into type_data, just like the current single-payload code).
export const fingerprintEffective = (effective) =>
  stableStringify({ ...effective, threadsReplies: toThreadsReplies(effective && effective.threadsReplies) });

// Groups selected accounts by their effective content.
// Returns an array of { accountIds: string[], effective: object }.
export const buildPostGroups = (state) => {
  const selectedAccounts = Array.isArray(state.selectedAccounts) ? state.selectedAccounts : [];
  const groups = new Map();

  for (const accountId of selectedAccounts) {
    const effective = getEffectiveData(state, accountId);
    const key = fingerprintEffective(effective);
    const existing = groups.get(key);
    if (existing) {
      existing.accountIds.push(accountId);
    } else {
      groups.set(key, { accountIds: [accountId], effective });
    }
  }

  return Array.from(groups.values());
};

// Returns the social_type set among the given account ids.
const getSocialTypesIn = (accounts, accountIds) => {
  const safe = Array.isArray(accounts) ? accounts : [];
  const set = new Set();
  for (const acc of safe) {
    if (acc && accountIds.includes(acc.id)) set.add(acc.social_type);
  }
  return set;
};

// Builds the type_data payload for a single group, mirroring the legacy
// `getPostData()` shape but using the group's effective content and only
// injecting platform-specific fields when the group actually contains an
// account of that type.
export const buildTypeDataForGroup = ({ effective, accountIds, accounts, formattedTags, files }) => {
  const types = getSocialTypesIn(accounts, accountIds);

  const typeData = {
    title: effective.postTitle || "",
    text: textWithTags(effective.postText, formattedTags),
    files: Array.isArray(effective.selectedFiles) ? [...effective.selectedFiles] : [],
  };

  if (types.has(SOCIAL_TYPES.YOUTUBE)) {
    typeData.privacy = effective.youtubePrivacy;
    typeData.category_id = effective.youtubeCategoryId;
  }

  if (types.has(SOCIAL_TYPES.TELEGRAM)) {
    typeData.pin_message = effective.telegramPinMessage;
    typeData.pin_silent = effective.telegramPinSilent;
    typeData.inline_keyboard = effective.telegramInlineKeyboard;
  }

  if (types.has(SOCIAL_TYPES.INSTAGRAM)) {
    typeData.post_type = effective.instagramPostType;
  }

  if (types.has(SOCIAL_TYPES.TIKTOK)) {
    typeData.tiktok = buildTikTokBlock(effective, typeData.files, files);
  }

  // Parts 2..N of a Threads chain. Only Threads accounts publish them; any other account in the same group
  // publishes part 1 and ignores the block, as with `tiktok`. Hashtags stay on part 1.
  const replies = toThreadsReplies(effective.threadsReplies);
  if (types.has(SOCIAL_TYPES.THREADS) && replies.length > 0) {
    typeData.threads = { replies };
  }

  return typeData;
};

// Detects whether the selected media for a TikTok post is a photo carousel
// (all images) vs a single video. Falls back to video if nothing is selected.
const isTikTokPhotoCarousel = (fileIds, allFiles) => {
  if (!Array.isArray(fileIds) || fileIds.length === 0) return false;
  if (!Array.isArray(allFiles)) return false;
  const byId = new Map(allFiles.map((f) => [f.id, f]));
  return fileIds.every((id) => {
    const f = byId.get(id);
    if (!f) return false;
    return f.file_type === "image" || (f.mime_type && f.mime_type.startsWith("image/"));
  });
};

// Builds the `type_data.tiktok` sub-object. In inbox mode everything except
// publish_mode is ignored by the backend, so we don't bother sending it.
const buildTikTokBlock = (effective, fileIds, allFiles) => {
  const publishMode = effective.tiktokPublishMode || "direct";

  if (publishMode === "inbox") {
    return { publish_mode: "inbox" };
  }

  const isPhoto = isTikTokPhotoCarousel(fileIds, allFiles);

  const block = {
    publish_mode: "direct",
    privacy_level: effective.tiktokPrivacyLevel,
    disable_comment: Boolean(effective.tiktokDisableComment),
    brand_content_toggle: Boolean(effective.tiktokBrandContentToggle),
    brand_organic_toggle: Boolean(effective.tiktokBrandOrganicToggle),
  };

  if (isPhoto) {
    block.photo_cover_index = Number.isInteger(effective.tiktokPhotoCoverIndex)
      ? effective.tiktokPhotoCoverIndex
      : 0;
    block.auto_add_music = effective.tiktokAutoAddMusic !== false;
  } else {
    block.disable_duet = Boolean(effective.tiktokDisableDuet);
    block.disable_stitch = Boolean(effective.tiktokDisableStitch);
    block.video_cover_timestamp_ms = Number.isInteger(effective.tiktokVideoCoverTimestampMs)
      ? effective.tiktokVideoCoverTimestampMs
      : 1000;
  }

  return block;
};
